/**
 * QA formatting helpers. Duration and timestamp rendering is shared with the
 * gen-api stress pages; only the status palette differs, because a QA run has an
 * `interrupted` state that a gen-api stress test does not.
 */

export { formatSeconds, formatDuration, formatTimestamp, formatClock } from '../apigen/stress/format';

import type { QaRunCost, QaRunStatus } from './types';

const STATUS_CLASSES: Record<QaRunStatus, string> = {
  pending: 'bg-slate-100 text-slate-600',
  running: 'bg-indigo-50 text-indigo-700',
  completed: 'bg-emerald-50 text-emerald-700',
  cancelled: 'bg-amber-50 text-amber-700',
  error: 'bg-red-50 text-red-700',
  // Distinct from error: the load was real, the schedule just never finished.
  interrupted: 'bg-orange-50 text-orange-700',
};

export function runStatusClasses(status: string): string {
  return STATUS_CLASSES[status as QaRunStatus] || 'bg-slate-100 text-slate-600';
}

/** Bursts needed to submit `count` units at `burstSize` each. Mirrors the backend. */
export function burstCount(count: number, burstSize: number): number {
  return Math.ceil(count / Math.max(1, Math.min(burstSize, count)));
}

/** Wall time the schedule alone needs, excluding the final delay. */
export function scheduleSeconds(count: number, burstSize: number, burstDelay: number): number {
  return Math.max(0, burstCount(count, burstSize) - 1) * burstDelay;
}

/** `1234` -> `1,234`, so per-application counts stay readable at scale. */
export function formatCount(value: number | null | undefined): string {
  return value == null ? '—' : value.toLocaleString();
}

/**
 * The build a run tested, as one short label.
 *
 * Normally every target is the same deployment and reports the same number, so
 * this collapses to a single version. Two are shown only when they genuinely
 * differed — gen-api deployed ahead of the backend, say — because that changes
 * how the numbers should be read.
 */
export function backendVersionLabel(versions: Record<string, string> | null | undefined): string {
  const distinct = [...new Set(Object.values(versions || {}).filter(Boolean))];
  return distinct.length === 0 ? '—' : distinct.sort().join(' / ');
}

/**
 * Spend, as dollars.
 *
 * Sub-cent amounts are the norm for a single call, so small figures keep four
 * decimals rather than rounding away to `$0.00`. A dummy run really did cost
 * nothing, so exact zero is shown as `$0` rather than as a dash — a dash means
 * "not known".
 */
export function formatUsd(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  if (value === 0) return '$0';
  if (value < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}

/**
 * What a run spent, with an explicit marker while the figure can still grow.
 *
 * A run reaches its terminal status when the last burst has been *submitted*,
 * and the work carries on generating afterwards — so a total that is not final
 * yet is labelled, rather than being presented as the answer.
 */
export function runCostLabel(cost: QaRunCost | null | undefined): string {
  if (!cost || cost.total == null) return '—';
  const amount = formatUsd(cost.total);
  return cost.final ? amount : `${amount}+`;
}
